import { RecordContextProvider, useListContext, useLocaleState } from "ra-core";
import { useNavigate } from "react-router";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Skeleton } from "@/components/ui/skeleton";

import { formatRelativeDate } from "../misc/RelativeDate";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";

/**
 * Gravun companies table. Columns are limited to fields the model actually
 * provides (companies_summary): primary-contact designation and pipeline
 * value aggregates are data-model gaps recorded in BUILD_LOG.md.
 */
export const CompanyListContent = () => {
  const { data: companies, error, isPending } = useListContext<Company>();
  const [locale = "en"] = useLocaleState();
  const navigate = useNavigate();

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (error) return null;

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <th className="px-4 py-2 font-medium">Company</th>
          <th className="px-3 py-2 font-medium">Phone</th>
          <th className="px-3 py-2 font-medium">Website</th>
          <th className="px-3 py-2 text-right font-medium">Contacts</th>
          <th className="px-3 py-2 text-right font-medium">Deals</th>
          <th className="px-3 py-2 font-medium">Owner</th>
          <th className="px-4 py-2 text-right font-medium">Created</th>
        </tr>
      </thead>
      <tbody>
        {companies.map((company) => (
          <RecordContextProvider key={company.id} value={company}>
            <tr
              className="cursor-pointer border-b border-border-subtle transition-colors last:border-0 hover:bg-surface-muted"
              onClick={() => navigate(`/companies/${company.id}/show`)}
            >
              <td className="px-4 py-2">
                <Link
                  to={`/companies/${company.id}/show`}
                  className="flex items-center gap-2.5 no-underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  <CompanyAvatar width={20} height={20} />
                  <span className="min-w-0 leading-tight">
                    <span className="block truncate text-[13px] font-medium text-foreground">
                      {company.name}
                    </span>
                    {company.sector ? (
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {company.sector}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-[13px] text-muted-foreground">
                {company.phone_number ?? "n/a"}
              </td>
              <td className="max-w-44 truncate px-3 py-2 text-[13px] text-muted-foreground">
                {company.website
                  ? String(company.website).replace(/^https?:\/\//, "")
                  : "n/a"}
              </td>
              <td className="px-3 py-2 text-right text-[13px] tabular-nums text-foreground">
                {company.nb_contacts ?? 0}
              </td>
              <td className="px-3 py-2 text-right text-[13px] tabular-nums text-foreground">
                {company.nb_deals ?? 0}
              </td>
              <td className="max-w-32 truncate px-3 py-2 text-[13px] text-muted-foreground">
                {company.sales_id != null ? (
                  <ReferenceField
                    source="sales_id"
                    reference="sales"
                    link={false}
                  >
                    <TextField source="first_name" />
                  </ReferenceField>
                ) : (
                  "n/a"
                )}
              </td>
              <td
                className="whitespace-nowrap px-4 py-2 text-right text-[12.5px] text-muted-foreground"
                title={company.created_at}
              >
                {company.created_at
                  ? formatRelativeDate(company.created_at, locale)
                  : "n/a"}
              </td>
            </tr>
          </RecordContextProvider>
        ))}
        {companies.length === 0 && (
          <tr>
            <td
              colSpan={7}
              className="p-6 text-center text-[13px] text-muted-foreground"
            >
              No companies found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
